import React, { useState } from 'react';
import { Settings, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { YearbookPage } from '@/types/yearbook';
import { editorUrl } from '@/lib/editorBasePath';
import { useToast } from '@/hooks/use-toast';

interface Props {
  pages: YearbookPage[];
  initialOnlyPageIds: number[];
}

const AdminSettingsMenu: React.FC<Props> = ({ pages, initialOnlyPageIds }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set(initialOnlyPageIds));
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleOpen = () => {
    setSelected(new Set(initialOnlyPageIds));
    setOpen(true);
  };

  const toggle = (pageNumber: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pageNumber)) next.delete(pageNumber);
      else next.add(pageNumber);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    const only_page_id = Array.from(selected).sort((a, b) => a - b);
    try {
      const res = await fetch(editorUrl('/add_only_page_id'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ only_page_id }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: 'Pages enregistrées' });
      setOpen(false);
      window.location.reload();
    } catch (e) {
      toast({ title: 'Erreur lors de l\'enregistrement', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="p-1.5 rounded text-editor-sidebar-foreground hover:bg-editor-sidebar-hover hover:text-editor-sidebar-active transition-colors"
            title="Paramètres"
          >
            <Settings size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleOpen}>Bloquer des pages</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md flex flex-col max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Afficher uniquement les pages :</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-2 grid grid-cols-3 gap-2">
            {pages.map((p) => {
              const label = p.coverType === 'front'
                ? '1ère de couv.'
                : p.coverType === 'back'
                ? '4ème de couv.'
                : p.coverType === 'tranche'
                ? 'Tranche'
                : `Page ${p.pageNumber}`;
              const checked = selected.has(p.pageNumber);
              return (
                <label
                  key={p.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent cursor-pointer text-xs"
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggle(p.pageNumber)} />
                  <span>{label}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-primary-gradient text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Enregistrer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminSettingsMenu;
